import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import { ALLOWED_CONTENT_TYPES, MAX_PRODUCTION_PHOTOS } from '@entities/media';
import { useProfileQuery } from '@entities/manufacturer';
import { MediaPicker } from '@features/media-picker';
import { EcoCertifications } from '../eco-certifications';
import type { ProfileFormValues } from '../../model/schema';

const { Text } = Typography;

export function MediaTab() {
  const { t } = useTranslation();
  const form = useFormContext<ProfileFormValues>();
  // We re-read the profile here so MediaPicker can resolve mediaId → URL via
  // the backend's MediaLink objects without an extra /download round-trip.
  const { data: profile } = useProfileQuery();
  const ecoProducer = form.watch('ecoProducer');

  const logoLinks = profile?.logo ? [profile.logo] : [];
  const videoLinks = profile?.video ? [profile.video] : [];
  const certLinks = profile?.certificates ?? [];
  const ecoCertLinks = profile?.ecoCertificates ?? [];
  const productionPhotoLinks = profile?.productionPhotos ?? [];

  return (
    <div className="flex flex-col gap-8">
      <Controller
        control={form.control}
        name="logo"
        render={({ field }) => (
          <MediaPicker
            scope="PROFILE_LOGO"
            label={t('profile.media.logo')}
            hint={t('profile.media.logoHint')}
            accept={ALLOWED_CONTENT_TYPES.image}
            multiple={false}
            value={field.value ? [field.value] : []}
            onChange={(ids) => field.onChange(ids[0] ?? null)}
            links={logoLinks}
          />
        )}
      />

      <Controller
        control={form.control}
        name="video"
        render={({ field }) => (
          <MediaPicker
            scope="PROFILE_VIDEO"
            label={t('profile.media.video')}
            hint={t('profile.media.videoHint')}
            accept={ALLOWED_CONTENT_TYPES.video}
            multiple={false}
            value={field.value ? [field.value] : []}
            onChange={(ids) => field.onChange(ids[0] ?? null)}
            links={videoLinks}
          />
        )}
      />

      <Controller
        control={form.control}
        name="productionPhotos"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <MediaPicker
              scope="PRODUCTION_PHOTO"
              label={t('profile.media.productionPhotos')}
              hint={t('profile.media.productionPhotosHint', { max: MAX_PRODUCTION_PHOTOS })}
              accept={ALLOWED_CONTENT_TYPES.image}
              multiple
              maxItems={MAX_PRODUCTION_PHOTOS}
              value={field.value}
              onChange={field.onChange}
              links={productionPhotoLinks}
            />
            {fieldState.error ? (
              <Text type="danger" style={{ fontSize: 12 }} role="alert">
                {fieldState.error.message}
              </Text>
            ) : null}
          </div>
        )}
      />

      <Controller
        control={form.control}
        name="certificates"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <MediaPicker
              scope="CERTIFICATE"
              label={t('profile.media.certificates')}
              hint={t('profile.media.certificatesHint')}
              accept={[...ALLOWED_CONTENT_TYPES.image, ...ALLOWED_CONTENT_TYPES.document]}
              multiple
              value={field.value}
              onChange={field.onChange}
              links={certLinks}
            />
            {fieldState.error ? (
              <Text type="danger" style={{ fontSize: 12 }} role="alert">
                {fieldState.error.message}
              </Text>
            ) : null}
          </div>
        )}
      />

      {ecoProducer ? (
        <Controller
          control={form.control}
          name="ecoCertificates"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-2">
              <MediaPicker
                scope="ECO_CERTIFICATE"
                label={t('profile.media.ecoCertificates')}
                hint={t('profile.media.ecoCertificatesHint')}
                accept={[...ALLOWED_CONTENT_TYPES.image, ...ALLOWED_CONTENT_TYPES.document]}
                multiple
                value={field.value}
                onChange={field.onChange}
                links={ecoCertLinks}
              />
              {fieldState.error ? (
                <Text type="danger" style={{ fontSize: 12 }} role="alert">
                  {fieldState.error.message}
                </Text>
              ) : null}
            </div>
          )}
        />
      ) : null}

      {/* Right under the scans: same subject, and the two are read together —
          the document proves what the row claims. */}
      {ecoProducer ? <EcoCertifications /> : null}
    </div>
  );
}
